import { SLIGHTLY_OVERUTILIZED_THRESHOLD } from '@constants'
import { filter, find, orderBy } from 'lodash-es'
import type { ShearAnchorPosition } from '@api/fastAPI.schemas'
import { ResultsViewTab, ResultsViewWallSubTab } from '@editorStores'
import { getSlabBeamCrossSectionFromAssembly } from 'src/utils/assemblies'

export type ScenePoint = { x: number; y: number; z: number }

/** Background of the orange chip behind an over-utilized η value (also used by the tab headers). */
export const OVERUTILIZED_CHIP_BACKGROUND = '#FFE3A1'

const SCENE_POINT_SUGGESTION_Z_THRESHOLD = 0.05

const isScenePoint = (point: unknown): point is ScenePoint => {
  if (point == null || typeof point !== 'object') return false
  const candidate = point as Partial<ScenePoint>
  return (
    typeof candidate.x === 'number' &&
    typeof candidate.y === 'number' &&
    typeof candidate.z === 'number'
  )
}

const midpoint = (start: ScenePoint, end: ScenePoint): ScenePoint => ({
  x: (start.x + end.x) / 2,
  y: (start.y + end.y) / 2,
  z: Math.max(start.z, end.z) + SCENE_POINT_SUGGESTION_Z_THRESHOLD,
})

const averagePoints = (points: ScenePoint[]): ScenePoint | null => {
  if (points.length === 0) return null
  const sum = points.reduce(
    (acc, point) => ({
      x: acc.x + point.x,
      y: acc.y + point.y,
      z: Math.max(acc.z, point.z),
    }),
    { x: 0, y: 0, z: -Infinity },
  )
  return {
    x: sum.x / points.length,
    y: sum.y / points.length,
    z: sum.z + SCENE_POINT_SUGGESTION_Z_THRESHOLD,
  }
}

export const getShapeCenterPoint = (element: unknown): ScenePoint | null => {
  if (element == null || typeof element !== 'object') return null

  const candidate = element as {
    shape?: {
      points?: unknown[]
      start?: unknown
      end?: unknown
    }
    start?: unknown
    end?: unknown
  }

  const shapePoints = candidate.shape?.points
  if (Array.isArray(shapePoints)) {
    return averagePoints(shapePoints.filter(isScenePoint))
  }

  const shapeStart = candidate.shape?.start
  const shapeEnd = candidate.shape?.end
  if (isScenePoint(shapeStart) && isScenePoint(shapeEnd)) return midpoint(shapeStart, shapeEnd)

  if (isScenePoint(candidate.start) && isScenePoint(candidate.end)) {
    return midpoint(candidate.start, candidate.end)
  }

  return null
}

export type SelectSupportTargetNavigation =
  | { kind: 'position'; guid: string; tab: ResultsViewTab; wallSubTab?: ResultsViewWallSubTab }
  | { kind: 'fallback'; guid: string }

/** Maps a vertically transmitted Auflager-Ziel element to Results2 tab / sub-tab selection. */
export const selectResultsNavigationForSupportTarget = ({
  guid,
  elementType,
  standardRipChecks,
}: {
  guid: string
  elementType?: ElementTypes
  standardRipChecks: StandardRipCheck[] | undefined | null
}): SelectSupportTargetNavigation => {
  if (standardRipChecks?.some(check => check.standard_rip_member.guid === guid)) {
    return { kind: 'position', guid, tab: 'walls', wallSubTab: 'standard-rips' }
  }

  switch (elementType) {
    case 'inner_walls':
    case 'outer_walls':
      return { kind: 'position', guid, tab: 'walls', wallSubTab: 'wall-assemblies' }
    case 'rips':
      return { kind: 'position', guid, tab: 'walls', wallSubTab: 'wall-rips' }
    case 'lintels':
      return { kind: 'position', guid, tab: 'walls', wallSubTab: 'wall-lintels' }
    case 'slab_beams':
      return { kind: 'position', guid, tab: 'slab-beams' }
    case 'roof_slab_beams':
      return { kind: 'position', guid, tab: 'roof-slab-beams' }
    case 'beams':
      return { kind: 'position', guid, tab: 'beams' }
    case 'purlins':
      return { kind: 'position', guid, tab: 'purlins' }
    case 'columns':
      return { kind: 'position', guid, tab: 'columns' }
    case 'vertical_slabs':
    case 'slabs':
      return { kind: 'position', guid, tab: 'slabs' }
    case 'vertical_roof_slabs':
    case 'roof_slabs':
      return { kind: 'position', guid, tab: 'roof-slabs' }
    default:
      return { kind: 'fallback', guid }
  }
}

export const getCommentValue = (value: unknown): string | undefined => {
  if (!value || typeof value !== 'object' || !('comment' in value)) return undefined
  const comment = (value as { comment?: unknown }).comment
  return typeof comment === 'string' ? comment : undefined
}

// Vibration is a binary check: max_utilization is 0 (passed) or 1 (failed). A value of 1.0
// therefore means the check errored out and should be surfaced as "ERR" rather than as a
// numeric utilization (which would be misleading for a non-continuous check).
export const isVibrationError = (
  checkType: MemberCheckType | undefined,
  maxUtilization: number | undefined,
): boolean => checkType === 'Vibration' && maxUtilization === 1.0

const roundDisplayedUtilization = (value: number): number =>
  Math.round((value + Number.EPSILON) * 100) / 100

/** η above the allowed limit up to the red threshold — slightly over-utilized (yellow / brown). */
export const isUtilizationWarning = (value: number, allowedOverutilizationPercent = 0): boolean => {
  const displayedValue = roundDisplayedUtilization(value)
  const allowance = Math.max(0, allowedOverutilizationPercent) / 100
  return (
    displayedValue > 1 + allowance && displayedValue <= SLIGHTLY_OVERUTILIZED_THRESHOLD + allowance
  )
}

/** η above allowed limit + 5% — over-utilized (red). */
export const isUtilizationError = (value: number, allowedOverutilizationPercent = 0): boolean => {
  const displayedValue = roundDisplayedUtilization(value)
  const allowance = Math.max(0, allowedOverutilizationPercent) / 100
  return displayedValue > SLIGHTLY_OVERUTILIZED_THRESHOLD + allowance
}

/**
 * Whether an element's η/ηc (with their allowed over-utilization tolerances) or a failed vibration
 * check put it into the red "err" state. Mirrors the `status === 'err'` branch used when building
 * the element list rows, so a tab colored from this stays in sync with its red rows.
 */
export const isResults2Error = (
  eta: number | undefined,
  etaC: number | undefined,
  etaAllowedOverutilizationPercent: number,
  etaCAllowedOverutilizationPercent: number,
  hasVibrationError: boolean,
): boolean =>
  hasVibrationError ||
  (eta !== undefined && isUtilizationError(eta, etaAllowedOverutilizationPercent)) ||
  (etaC !== undefined && isUtilizationError(etaC, etaCAllowedOverutilizationPercent))

/** Collapses beam/slab subtabs to the primary icon strip id (`PRIMARY_TABS`). */
export const resultsTabToPrimary = (tab: ResultsViewTab): ResultsViewTab => {
  if (tab === 'slab-beams' || tab === 'slabs') return 'slabs'
  if (tab === 'roof-slab-beams' || tab === 'roof-slabs') return 'roof-slabs'
  return tab
}

/** Primary icon strip id -> default results store tab. */
export const primaryToResultsTab = (primary: ResultsViewTab): ResultsViewTab => {
  // Decke/Dach no longer have a surface list; they go straight to their beams.
  if (primary === 'slabs') return 'slab-beams'
  if (primary === 'roof-slabs') return 'roof-slab-beams'
  return primary
}

/** Active store tab -> secondary icon strip id (must match list pane constants). */
export const resultsTabToSecondary = (
  tab: ResultsViewTab,
  wallSubTab: ResultsViewWallSubTab,
): string => {
  if (tab === 'walls') return wallSubTab
  if (tab === 'slab-beams') return 'slab-beams'
  if (tab === 'slabs') return 'slabs-assemblies'
  if (tab === 'roof-slab-beams') return 'roof-beams'
  if (tab === 'roof-slabs') return 'roof-assemblies'
  return ''
}

export const secondaryToWallSubTab = (secondary: string): ResultsViewWallSubTab =>
  secondary as ResultsViewWallSubTab

export const getStandardRipMemberGuidForWall = (
  standardRipChecks: StandardRipCheck[] | undefined | null,
  wallGuid: string,
): string | undefined => {
  const checksForWall = filter(standardRipChecks, ripCheck => ripCheck.wall_guid === wallGuid)
  if (checksForWall.length !== 1) return undefined
  return checksForWall[0].standard_rip_member.guid
}

export const getWallGuidForStandardRipMember = (
  standardRipChecks: StandardRipCheck[] | undefined | null,
  standardRipMemberGuid: string,
): string | undefined =>
  find(standardRipChecks, check => check.standard_rip_member.guid === standardRipMemberGuid)
    ?.wall_guid

export const getStiffeningSegmentGuidForWall = (
  stiffeningIntervals: StiffeningSegment[] | undefined | null,
  wallGuid: string,
): string | undefined => find(stiffeningIntervals, { element_guid: wallGuid })?.guid

/** Position of a tensile anchor on its element, as shown in the "Anker" tab. */
export type AnchorSide = 'links' | 'rechts' | 'unten'

/** Compact side suffix used in the anchor overview list ("00.W01.01 · li"). */
export const ANCHOR_SIDE_SHORT_LABEL: Record<AnchorSide, string> = {
  links: 'li',
  rechts: 're',
  unten: 'unten',
}

/** Compact position suffix used in the Schubanker list ("00.W01.A01 · Fuß"). */
export const SHEAR_ANCHOR_POSITION_SHORT_LABEL: Record<ShearAnchorPosition, string> = {
  bottom: 'Fuß',
  top: 'Kopf',
}

const SHEAR_ANCHOR_ROW_ID_SEPARATOR = '#'

/**
 * Row id of one shear anchor: a stiffening segment is anchored at its foot and at its head, each
 * with its own anchor, and the Schubanker list has a row per anchor. The selection stores the
 * segment guid and the position separately; the row id joins them.
 */
export const shearAnchorRowId = (segmentGuid: string, position: ShearAnchorPosition): string =>
  `${segmentGuid}${SHEAR_ANCHOR_ROW_ID_SEPARATOR}${position}`

/** The stiffening segment guid of a Schubanker row id; a plain segment guid passes through. */
export const shearAnchorRowSegmentGuid = (rowId: string): string =>
  rowId.split(SHEAR_ANCHOR_ROW_ID_SEPARATOR)[0]

/** The position of a Schubanker row id; undefined for a plain segment guid. */
export const shearAnchorRowPosition = (rowId: string): ShearAnchorPosition | undefined => {
  const position = rowId.split(SHEAR_ANCHOR_ROW_ID_SEPARATOR)[1]
  return position === 'bottom' || position === 'top' ? position : undefined
}

/** The Schubanker location a row id names; a plain segment guid is its foot. */
export const shearAnchorLocationOfRowId = (rowId: string): ShearAnchorLocationSelection => ({
  guid: shearAnchorRowSegmentGuid(rowId),
  position: shearAnchorRowPosition(rowId) ?? 'bottom',
})

/**
 * Derives the side label per anchor support: anchors without a stiffening segment (columns,
 * single rips) are "unten"; anchors sharing a segment are ordered by their relative position on
 * the wall ("links" first); a lone segment anchor is placed against the segment's midpoint.
 */
export const getAnchorSides = (
  rows: AnchorInterventionsTableRowData[],
  supportRelativePositions: Record<string, number | undefined>,
  segmentMidpoints: Record<string, number | undefined>,
): Record<string, AnchorSide> => {
  const sides: Record<string, AnchorSide> = {}
  const rowsBySegment = new Map<string, AnchorInterventionsTableRowData[]>()

  for (const row of rows) {
    if (!row.segment_guid) {
      sides[row.support_guid] = 'unten'
      continue
    }
    const segmentRows = rowsBySegment.get(row.segment_guid) ?? []
    segmentRows.push(row)
    rowsBySegment.set(row.segment_guid, segmentRows)
  }

  for (const [segmentGuid, segmentRows] of rowsBySegment) {
    if (segmentRows.length === 1) {
      const position = supportRelativePositions[segmentRows[0].support_guid]
      const midpoint = segmentMidpoints[segmentGuid] ?? 0.5
      sides[segmentRows[0].support_guid] =
        position !== undefined && position > midpoint ? 'rechts' : 'links'
      continue
    }
    const ordered = orderBy(
      segmentRows,
      row => supportRelativePositions[row.support_guid] ?? 0,
      'asc',
    )
    ordered.forEach((row, index) => {
      sides[row.support_guid] = index === ordered.length - 1 ? 'rechts' : 'links'
    })
  }

  return sides
}

export const getWallGuidForStiffeningSegment = (
  stiffeningIntervals: StiffeningSegment[] | undefined | null,
  stiffeningSegmentGuid: string,
): string | undefined => find(stiffeningIntervals, { guid: stiffeningSegmentGuid })?.element_guid

/** Cross section used for list QS and “copy parent CS” (matches old Results grid behavior). */
export const resolveResults2ListElementCrossSection = (
  elementId: string,
  tab: ResultsViewTab,
  assignmentByGuid: Record<string, ElementCSAssignment | undefined>,
  slabBeamGuidToAssembly: Record<string, Assembly | undefined>,
): CrossSection | CLTCrossSection | undefined => {
  const isSlabBeamTab = tab === 'slab-beams' || tab === 'roof-slab-beams'
  if (isSlabBeamTab) {
    const assembly = slabBeamGuidToAssembly[elementId]
    if (assembly) {
      try {
        return getSlabBeamCrossSectionFromAssembly(assembly)
      } catch {
        // fall through to assignment
      }
    }
  }
  return assignmentByGuid[elementId]?.element_cs
}
